// Search behaviour for every [data-pc-search] form (header + search page):
// 1. a barcode / SKU opens its product directly when exactly one product matches (DESIGN.md §8);
// 2. as-you-type dropdown rendered by sections/pc-predictive-search (pc-card → quote prices stay hidden).
// Any failure falls through to the normal search: the user is never prevented from searching.
import { isCodeQuery, pickExactMatch, scopedQuery, plainTerms } from '@pc/search-core'; // versioned via the import map (snippets/scripts.liquid): a bare './x.js' import is served stale by the CDN

async function rawDropdown(q, signal) {
  const params = new URLSearchParams({ q, type: 'product', section_id: 'pc-search-dropdown' });
  const res = await fetch(`/search?${params}`, { signal });
  return res.ok ? res.text() : '';
}

// as-you-type: title matches first, the normal (looser) search only when nothing matches by title
async function dropdownHtml(q, signal) {
  const scoped = scopedQuery(q);
  if (scoped) {
    const html = await rawDropdown(scoped, signal);
    if (html.includes('data-handle=')) return html;
  }
  return rawDropdown(q, signal);
}

const searchUrl = (q, extra = '') => `/search?${new URLSearchParams({ q, type: 'product' })}${extra}`;

async function exactCodeUrl(q) {
  // predictive search is unavailable for he-IL (HTTP 417), so take the regular search's first hits
  const doc = new DOMParser().parseFromString(await rawDropdown(q), 'text/html');
  const handles = [...doc.querySelectorAll('[data-handle]')].map((e) => e.dataset.handle).slice(0, 6);
  const products = await Promise.all(handles.map(async (handle) => {
    const r = await fetch(`/products/${handle}.js`);
    const full = r.ok ? await r.json() : { variants: [] };
    return { url: `/products/${handle}`, variants: full.variants || [] };
  }));
  return pickExactMatch(products, q);
}

function wire(form) {
  const input = form.querySelector('input[name="q"]');
  const box = form.querySelector('[data-pc-search-results]');
  let going = false;
  addEventListener('pageshow', () => { going = false; }); // back button (bfcache) must not disable the lookup

  form.addEventListener('submit', async (e) => {
    const q = input.value;
    if (going) return;
    if (!isCodeQuery(q)) {
      const scoped = scopedQuery(q);
      if (scoped) { e.preventDefault(); going = true; location.assign(searchUrl(scoped)); }
      return;
    }
    e.preventDefault();
    going = true;
    try {
      const url = await exactCodeUrl(q);
      if (url) { location.assign(url); return; }
    } catch { /* fall through to normal search */ }
    form.submit();
  });

  if (!box) return;
  let timer;
  let ctrl;
  input.setAttribute('aria-controls', box.id || (box.id = `pc-sr-${Math.random().toString(36).slice(2)}`));
  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { ctrl?.abort(); show(false); box.innerHTML = ''; return; }
    timer = setTimeout(async () => {
      ctrl?.abort();
      ctrl = new AbortController();
      try {
        const html = await dropdownHtml(q, ctrl.signal);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-pc-predictive]');
        box.innerHTML = inner ? inner.outerHTML : '';
        box.querySelectorAll('[role="option"]').forEach((o, i) => { o.id = `${box.id}-o${i}`; });
        show(!!inner);
      } catch { /* aborted or offline: keep the box as is */ }
    }, 200);
  });

  // combobox: arrows move through the results, Enter opens the highlighted one, Esc closes
  let active = -1;
  function show(open) {
    box.hidden = !open;
    input.setAttribute('aria-expanded', String(open));
    if (!open) highlight(-1);
  }
  function highlight(i) {
    const opts = [...box.querySelectorAll('[role="option"]')];
    active = opts.length ? Math.max(-1, Math.min(i, opts.length - 1)) : -1;
    opts.forEach((o, j) => { o.classList.toggle('is-active', j === active); o.setAttribute('aria-selected', String(j === active)); });
    if (active >= 0) { input.setAttribute('aria-activedescendant', opts[active].id); opts[active].scrollIntoView({ block: 'nearest' }); }
    else input.removeAttribute('aria-activedescendant');
  }
  input.addEventListener('keydown', (e) => {
    if (box.hidden) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
    else if (e.key === 'Enter' && active >= 0) {
      const link = box.querySelectorAll('[role="option"]')[active]?.querySelector('a');
      if (link) { e.preventDefault(); location.assign(link.href); }
    }
  });
  form.addEventListener('keydown', (e) => { if (e.key === 'Escape') { show(false); input.focus(); } });
  document.addEventListener('click', (e) => { if (!form.contains(e.target)) show(false); });
}

document.querySelectorAll('[data-pc-search]').forEach(wire);

// /search?q=<code> opened directly (link, bookmark, browser bar): same exact-match jump, once per query
if (location.pathname === '/search') {
  const sp = new URLSearchParams(location.search);
  const q = sp.get('q') || '';
  // came back empty from the title search: say so above the looser results
  if (sp.get('fb') === '1') document.querySelectorAll('[data-pc-fb]').forEach((n) => { n.hidden = false; });
  // a plain query opened directly (Google, bookmark): use the title search too, unless it already fell back
  // (only a bare ?q=…&type=…: once filters, sort or paging are in the URL the shopper is already browsing)
  else if (q && !isCodeQuery(q) && scopedQuery(q) && [...sp.keys()].every((k) => k === 'q' || k === 'type')) {
    location.replace(searchUrl(scopedQuery(q)));
  }
  const key = `pc-sku-jump:${q}`;
  if (isCodeQuery(q) && !sessionStorage.getItem(key)) {
    try { sessionStorage.setItem(key, '1'); } catch { /* private mode */ }
    exactCodeUrl(q).then((url) => { if (url) location.replace(url); }).catch(() => {});
  }
}
